import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';

/**
 * Itinerary children: days, components, approvals, revisions, share viewer.
 * Phase B4.2 through B4.4.
 *
 * Component source rule per migration plan FAQ #8 is enforced server-side:
 *   HOTEL    -> DB or TBO
 *   FLIGHT   -> TBO only (no DB fallback)
 *   ACTIVITY -> DB only
 *   TRANSFER -> DB only
 *   VAS      -> DB only
 */

// ============================================================================
// Component kinds + sources
// ============================================================================

export const ItineraryComponentKindSchema = z.enum([
  'FLIGHT',
  'HOTEL',
  'TRANSFER',
  'ACTIVITY',
  'VAS',
  'CRUISE',
]);
export type ItineraryComponentKind = z.infer<typeof ItineraryComponentKindSchema>;

export const ItineraryComponentSourceSchema = z.enum(['DB', 'TBO']);
export type ItineraryComponentSource = z.infer<typeof ItineraryComponentSourceSchema>;

export const ItineraryComponentStatusSchema = z.enum([
  'PROPOSED',
  'CONFIRMED',
  'BOOKED',
  'CANCELLED',
  'ON_HOLD',
]);
export type ItineraryComponentStatus = z.infer<typeof ItineraryComponentStatusSchema>;

// ============================================================================
// Days
// ============================================================================

export const ItineraryDayOutSchema = z.object({
  id: UuidSchema,
  dayIndex: z.number().int(),
  date: z.string().nullable(),
  destinationCityId: UuidSchema.nullable(),
  destinationCityName: z.string().nullable(),
  mealPlan: z.string().nullable(),
  title: z.string().nullable(),
  briefHtml: z.string().nullable(),
  /** Gallery images for the share page / docket (first = day hero). */
  imageUrls: z.array(z.string()),
  componentCount: z.number().int(),
});
export type ItineraryDayOut = z.infer<typeof ItineraryDayOutSchema>;

export const CreateItineraryDayRequestSchema = z.object({
  dayIndex: z.number().int().min(1).max(365),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  destinationCityId: UuidSchema.nullable().optional(),
  mealPlan: z.string().trim().max(120).nullable().optional(),
  title: z.string().trim().max(200).nullable().optional(),
  briefHtml: z.string().max(100_000).nullable().optional(),
  imageUrls: z.array(z.string().trim().url().max(2000)).max(12).optional(),
});
export type CreateItineraryDayRequest = z.infer<typeof CreateItineraryDayRequestSchema>;

export const UpdateItineraryDayRequestSchema = CreateItineraryDayRequestSchema.partial();
export type UpdateItineraryDayRequest = z.infer<typeof UpdateItineraryDayRequestSchema>;

// ============================================================================
// Components
// ============================================================================

/** How an ACTIVITY component handles its transfer (mirrors the Prisma enum). */
export const ItineraryActivityTransferModeSchema = z.enum(['NONE', 'INCLUDED', 'SIC', 'PRIVATE']);
export type ItineraryActivityTransferMode = z.infer<typeof ItineraryActivityTransferModeSchema>;

export const ItineraryComponentOutSchema = z.object({
  id: UuidSchema,
  itineraryId: UuidSchema,
  dayId: UuidSchema.nullable(),
  kind: ItineraryComponentKindSchema,
  source: ItineraryComponentSourceSchema,
  status: ItineraryComponentStatusSchema,
  sortOrder: z.number().int(),
  title: z.string(),
  descriptionHtml: z.string().nullable(),
  // Source/kind-specific FKs (each kind populates one cluster)
  hotelId: UuidSchema.nullable(),
  tboHotelCode: z.string().nullable(),
  cruiseId: UuidSchema.nullable(),
  cruiseCabinId: UuidSchema.nullable(),
  airlineId: UuidSchema.nullable(),
  activityId: UuidSchema.nullable(),
  activityVariantId: UuidSchema.nullable(),
  activityTransferMode: ItineraryActivityTransferModeSchema.nullable(),
  transferId: UuidSchema.nullable(),
  transferRateId: UuidSchema.nullable(),
  visaId: UuidSchema.nullable(),
  mealServiceId: UuidSchema.nullable(),
  supplierId: UuidSchema.nullable(),
  mealPlanId: UuidSchema.nullable(),
  roomTypeId: UuidSchema.nullable(),
  occupancyTypeId: UuidSchema.nullable(),
  vehicleTypeId: UuidSchema.nullable(),
  // Generic
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  fromLocation: z.string().nullable(),
  toLocation: z.string().nullable(),
  pnr: z.string().nullable(),
  supplierRef: z.string().nullable(),
  isRefundable: z.boolean(),
  costPaise: z.string(),
  markupPaise: z.string(),
  cancellationChargePaise: z.string(),
  /** Kind-specific extras (TBO resultIndex/traceId/segments, room category, etc.). */
  metaJson: z.unknown().nullable(),
});
export type ItineraryComponentOut = z.infer<typeof ItineraryComponentOutSchema>;

const PaiseStringSchema = z.string().regex(/^-?\d+$/, 'Paise must be a whole-number string');

const ItineraryComponentCoreFieldsSchema = z.object({
  kind: ItineraryComponentKindSchema,
  source: ItineraryComponentSourceSchema.default('DB'),
  title: z.string().trim().min(1).max(300),
  descriptionHtml: z.string().max(100_000).nullable().optional(),
  // Source/kind-specific FKs
  hotelId: UuidSchema.nullable().optional(),
  tboHotelCode: z.string().trim().min(1).max(64).nullable().optional(),
  cruiseId: UuidSchema.nullable().optional(),
  cruiseCabinId: UuidSchema.nullable().optional(),
  airlineId: UuidSchema.nullable().optional(),
  activityId: UuidSchema.nullable().optional(),
  activityVariantId: UuidSchema.nullable().optional(),
  activityTransferMode: ItineraryActivityTransferModeSchema.nullable().optional(),
  transferId: UuidSchema.nullable().optional(),
  transferRateId: UuidSchema.nullable().optional(),
  visaId: UuidSchema.nullable().optional(),
  mealServiceId: UuidSchema.nullable().optional(),
  supplierId: UuidSchema.nullable().optional(),
  mealPlanId: UuidSchema.nullable().optional(),
  roomTypeId: UuidSchema.nullable().optional(),
  occupancyTypeId: UuidSchema.nullable().optional(),
  vehicleTypeId: UuidSchema.nullable().optional(),
  // Generic
  sortOrder: z.number().int().min(0).optional(),
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  endDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  startTime: z.string().trim().max(8).nullable().optional(),
  endTime: z.string().trim().max(8).nullable().optional(),
  fromLocation: z.string().trim().max(200).nullable().optional(),
  toLocation: z.string().trim().max(200).nullable().optional(),
  pnr: z.string().trim().max(20).nullable().optional(),
  supplierRef: z.string().trim().max(200).nullable().optional(),
  isRefundable: z.boolean().optional(),
  costPaise: PaiseStringSchema.optional(),
  markupPaise: PaiseStringSchema.optional(),
  cancellationChargePaise: PaiseStringSchema.optional(),
  /** Kind-specific extras (TBO resultIndex/traceId/segments, room category, etc.). */
  metaJson: z.record(z.string(), z.unknown()).nullable().optional(),
});

export const CreateItineraryComponentRequestSchema = ItineraryComponentCoreFieldsSchema;
export type CreateItineraryComponentRequest = z.infer<typeof CreateItineraryComponentRequestSchema>;

export const UpdateItineraryComponentRequestSchema = ItineraryComponentCoreFieldsSchema.partial();
export type UpdateItineraryComponentRequest = z.infer<typeof UpdateItineraryComponentRequestSchema>;

// ---- Auto-search preview (resync) -------------------------------------------

/** Which kinds to (re)fetch from TBO without persisting; used by Resync. */
export const ItineraryAutoSearchPreviewRequestSchema = z.object({
  flights: z.boolean().optional().default(true),
  hotels: z.boolean().optional().default(true),
  cruise: z.boolean().optional().default(true),
});
export type ItineraryAutoSearchPreviewRequest = z.infer<
  typeof ItineraryAutoSearchPreviewRequestSchema
>;

/** One proposed component + the day it belongs on (not yet persisted). */
export const ItineraryAutoSearchProposalSchema = z.object({
  dayId: UuidSchema,
  component: CreateItineraryComponentRequestSchema,
});
export type ItineraryAutoSearchProposal = z.infer<typeof ItineraryAutoSearchProposalSchema>;

export const ItineraryAutoSearchPreviewResponseSchema = z.object({
  proposals: z.array(ItineraryAutoSearchProposalSchema),
  /** Existing hotel component ids to REMOVE (blank): the hotel on that day is
   *  unavailable for the new pax/dates and no fallback could be priced. */
  removeComponentIds: z.array(UuidSchema).default([]),
});
export type ItineraryAutoSearchPreviewResponse = z.infer<
  typeof ItineraryAutoSearchPreviewResponseSchema
>;

/**
 * Reconcile diff: when the trip shape changes (cities added/removed/reordered,
 * dates changed), `toAdd` are the components to add (new-city hotels, internal
 * flights, a re-searched round-trip) and `toRemoveIds` are the stale/orphan
 * components to drop. Components that are still valid are not listed - the
 * caller keeps them (preserving manual picks).
 */
export const ItineraryReconcileResponseSchema = z.object({
  toAdd: z.array(ItineraryAutoSearchProposalSchema),
  toRemoveIds: z.array(UuidSchema),
});
export type ItineraryReconcileResponse = z.infer<typeof ItineraryReconcileResponseSchema>;

// ============================================================================
// Approval chain
// ============================================================================

export const ItineraryApprovalTeamSchema = z.enum([
  'AGENT',
  'OPS_QC',
  'FLIGHT',
  'HOTEL',
  'ACTIVITY',
  'TRANSFER',
  'VAS',
  'DOCKET',
]);
export type ItineraryApprovalTeam = z.infer<typeof ItineraryApprovalTeamSchema>;

export const ItineraryApprovalStatusSchema = z.enum([
  'PENDING',
  'IN_PROGRESS',
  'APPROVED',
  'REJECTED',
  'REVERTED',
  'ON_HOLD',
  'REVERTED_BY_LEAD_EDIT',
]);
export type ItineraryApprovalStatus = z.infer<typeof ItineraryApprovalStatusSchema>;

export const ItineraryApprovalOutSchema = z.object({
  id: UuidSchema,
  stepNumber: z.number().int(),
  assignedTeam: ItineraryApprovalTeamSchema,
  fromStatus: ItineraryApprovalStatusSchema.nullable(),
  toStatus: ItineraryApprovalStatusSchema,
  actorUserId: UuidSchema,
  actorName: z.string().nullable(),
  comment: z.string().nullable(),
  changedAt: IsoDateTimeSchema,
});
export type ItineraryApprovalOut = z.infer<typeof ItineraryApprovalOutSchema>;

export const ApprovalActionSchema = z.enum(['SUBMIT', 'APPROVE', 'REJECT', 'REVERT']);
export type ApprovalAction = z.infer<typeof ApprovalActionSchema>;

export const ItineraryApprovalActionRequestSchema = z.object({
  action: ApprovalActionSchema,
  comment: z.string().trim().max(2000).optional(),
});
export type ItineraryApprovalActionRequest = z.infer<typeof ItineraryApprovalActionRequestSchema>;

export const ItineraryApprovalListResponseSchema = z.object({
  data: z.array(ItineraryApprovalOutSchema),
  /** The current step + assigned team, derived from the latest history row. */
  currentStep: z.number().int(),
  currentTeam: ItineraryApprovalTeamSchema,
  currentStatus: ItineraryApprovalStatusSchema,
});
export type ItineraryApprovalListResponse = z.infer<typeof ItineraryApprovalListResponseSchema>;

// ============================================================================
// Publish / Revisions / Share
// ============================================================================

export const ItineraryRevisionOutSchema = z.object({
  id: UuidSchema,
  revisionNo: z.number().int(),
  publishedVersion: z.number().int().nullable(),
  shareToken: z.string().nullable(),
  publishedAt: IsoDateTimeSchema.nullable(),
  publishedById: UuidSchema.nullable(),
  acceptedAt: IsoDateTimeSchema.nullable(),
  acceptedBy: z.string().nullable(),
  changeNote: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type ItineraryRevisionOut = z.infer<typeof ItineraryRevisionOutSchema>;

export const PublishItineraryRequestSchema = z.object({
  changeSummary: z.string().trim().max(500).optional(),
  /** One-shot token from `publish/check` when live prices moved. */
  repriceToken: z.string().trim().min(1).max(128).optional(),
});
export type PublishItineraryRequest = z.infer<typeof PublishItineraryRequestSchema>;

export const PublishItineraryResponseSchema = z.object({
  revisionId: UuidSchema,
  publishedVersion: z.number().int(),
  shareToken: z.string(),
  shareUrl: z.string(),
});
export type PublishItineraryResponse = z.infer<typeof PublishItineraryResponseSchema>;

/**
 * Payment plan view shown on the customer share page (Phase 1 - Payment
 * Instalment Policy §8 of the plan doc). Nullable when the itinerary is
 * not eligible for the policy (non-lead-linked, non-INR, or no
 * travel_start_date), in which case the share page falls back to the
 * legacy full-payment card only.
 */
/**
 * How long a payment window stays open, in hours (2026-10-19 rule):
 *   - Instalment 1: from the moment the customer ACCEPTS the itinerary.
 *   - Every later instalment: from its due date.
 * Past that, the instalment lapses and the share page shows "Window closed".
 */
export const PAYMENT_WINDOW_CLOSE_HOURS = 72;

const IST_OFFSET_MS = 330 * 60 * 1000;

/**
 * When a payment window closes: the END of the IST day (23:59:59.999 IST) on
 * which `anchor + PAYMENT_WINDOW_CLOSE_HOURS` falls, so a customer always gets
 * until 11:59 PM IST on the last day instead of an odd clock time (e.g. 5:30 AM
 * from a date-only due date stored at 00:00 UTC). Monotonic in `anchor`: a
 * later anchor never closes earlier. Used by the share page AND the lapse /
 * default crons so what the customer sees is exactly when it lapses.
 */
export const paymentWindowClosesAt = (anchor: Date): Date => {
  const istWallClock = new Date(
    anchor.getTime() + PAYMENT_WINDOW_CLOSE_HOURS * 60 * 60 * 1000 + IST_OFFSET_MS,
  );
  return new Date(
    Date.UTC(
      istWallClock.getUTCFullYear(),
      istWallClock.getUTCMonth(),
      istWallClock.getUTCDate(),
      23,
      59,
      59,
      999,
    ) - IST_OFFSET_MS,
  );
};

export const SharePaymentPlanInstalmentSchema = z.object({
  id: z.string(),
  seq: z.number().int(),
  label: z.string(),
  amountPaise: z.string(),
  currencyCode: z.string(),
  dueDate: IsoDateTimeSchema,
  isNonRefundable: z.boolean(),
  status: z.enum(['PENDING', 'ACTIVE', 'PAID', 'LAPSED', 'CANCELLED']),
  paidAt: IsoDateTimeSchema.nullable(),
  paymentUrl: z.string().nullable(),
  /**
   * When this instalment's window closes (see PAYMENT_WINDOW_CLOSE_HOURS).
   * Null when paid / closed, or for I1 before the itinerary is accepted.
   */
  windowClosesAt: IsoDateTimeSchema.nullable().optional(),
});
export type SharePaymentPlanInstalment = z.infer<typeof SharePaymentPlanInstalmentSchema>;

export const SharePaymentPlanSchema = z.object({
  id: z.string(),
  status: z.enum(['PREVIEW', 'LOCKED', 'LAPSED', 'DEFAULTED', 'COMPLETED', 'CANCELLED']),
  tier: z.number().int(),
  currencyCode: z.string(),
  totalSalePaise: z.string(),
  bookingAmountPaise: z.string(),
  tcsPaise: z.string(),
  publishedAt: IsoDateTimeSchema,
  travelStartDate: IsoDateTimeSchema,
  daysToDeparture: z.number().int(),
  lockedAt: IsoDateTimeSchema.nullable(),
  completedAt: IsoDateTimeSchema.nullable(),
  cancelledAt: IsoDateTimeSchema.nullable(),
  cancellationReason: z.string().nullable(),
  flightWindowHours: z.number().int(),
  instalments: z.array(SharePaymentPlanInstalmentSchema),
});
export type SharePaymentPlan = z.infer<typeof SharePaymentPlanSchema>;

/**
 * Live status of the full-payment `ItineraryPaymentLink`. Separate from
 * the snapshot so the share page can render Paid / Cancelled states even
 * when the URL is no longer usable. Null when no full-payment link
 * exists.
 */
export const ShareFullPaymentSchema = z.object({
  status: z.enum(['PENDING', 'ACTIVE', 'PAID', 'FAILED', 'CANCELLED']),
  paidAt: IsoDateTimeSchema.nullable(),
  paidAmountPaise: z.string().nullable(),
  currencyCode: z.string(),
  /**
   * Payable after the pay-in-full "Cash Discount". Equals `originalAmountPaise`
   * when no discount applies. This is the number the customer actually pays.
   */
  amountPaise: z.string().nullable().optional(),
  /**
   * Undiscounted grand total (pre cash-discount) for the struck-through price.
   * Null on legacy links minted before the discount existed - render no strike.
   */
  originalAmountPaise: z.string().nullable().optional(),
  /** The cash discount itself in paise (0 when none), for the "You save" line. */
  cashDiscountPaise: z.string().nullable().optional(),
  /** Snapshotted cash-discount rate as a percent (e.g. 0.5), for the label. */
  cashDiscountRatePercent: z.number().nullable().optional(),
  /**
   * Soft "pay by" date for the full-payment option (publishedAt + config days).
   * Informational only - the discount stays valid the whole link life. Null
   * when unknown (legacy links / no publishedAt).
   */
  dueAt: IsoDateTimeSchema.nullable().optional(),
});
export type ShareFullPayment = z.infer<typeof ShareFullPaymentSchema>;

/** Public share-viewer response (frozen snapshot). */
export const ItineraryShareViewSchema = z.object({
  publishedVersion: z.number().int(),
  /**
   * 2026-07-15 (ADR-0065). Quote No. (= publishedVersion) and full Quote ID
   * `UTZ<year>/QUOTE/<version>` for the share page's top label. Additive +
   * optional so an already-cached older client keeps parsing. Never null on a
   * live share view (the revision is always published here), but kept nullable
   * for defensive parity with the rest of the quote-id surfaces.
   */
  quoteNo: z.number().int().nullable().optional(),
  quoteId: z.string().nullable().optional(),
  itineraryTitle: z.string(),
  publishedAt: IsoDateTimeSchema,
  /** Frozen JSON snapshot of the itinerary at publish time. */
  snapshot: z.unknown(),
  acceptedAt: IsoDateTimeSchema.nullable(),
  /** Live payment plan for the customer (Phase 1 §8). Null when the
   *  itinerary is ineligible for the policy. */
  paymentPlan: SharePaymentPlanSchema.nullable().optional(),
  /** Live full-payment link status. Null when no link exists yet. */
  fullPayment: ShareFullPaymentSchema.nullable().optional(),
  /** Itinerary flipped to CONVERTED_TO_BOOKING. Powers the top-of-page
   *  "Booking Confirmed" banner. Either the full-payment link succeeded
   *  or the plan's I1 did - both flip this to true. */
  bookingConfirmed: z.boolean().optional(),
  /**
   * True only on the agent-facing pre-publish preview (`GET
   * /share/p/:slug`). The page renders identically to the real
   * customer view but every customer action (Accept, Request changes, Pay
   * Now) is inert, because nothing has been published yet. Absent/false on
   * every real share view.
   */
  previewMode: z.boolean().optional(),
});
export type ItineraryShareView = z.infer<typeof ItineraryShareViewSchema>;

/**
 * Pre-publish preview link (agent-facing). Minted by `POST
 * /itineraries/:id/preview-link`; opens the customer share page rendered
 * from the LIVE draft with zero side effects - no revision, no share token,
 * no payment link, no email. Expires on its own.
 */
export const ItineraryPreviewLinkResponseSchema = z.object({
  previewToken: z.string(),
  previewUrl: z.string(),
  expiresAt: IsoDateTimeSchema,
  /** The version number this draft WOULD publish as. */
  publishedVersionPreview: z.number().int(),
  /**
   * False when the Pricing step was never saved. Publish auto-applies the
   * suggested markup in that case, so the preview's totals are the raw
   * pre-markup numbers and the viewer shows a warning strip.
   */
  pricingSaved: z.boolean(),
});
export type ItineraryPreviewLinkResponse = z.infer<typeof ItineraryPreviewLinkResponseSchema>;

export const ShareAcceptRequestSchema = z.object({
  acceptedBy: z.string().trim().min(1).max(200),
});
export type ShareAcceptRequest = z.infer<typeof ShareAcceptRequestSchema>;

export const ShareRequestChangesRequestSchema = z.object({
  requestedBy: z.string().trim().min(1).max(200),
  message: z.string().trim().min(1).max(2000),
});
export type ShareRequestChangesRequest = z.infer<typeof ShareRequestChangesRequestSchema>;

// ---- Pay Now live reprice (customer share page) ---------------------------

/**
 * One flight/hotel line of the Pay-Now reprice check. `appliedCostPaise` is
 * the price that will be charged: the latest live price when it went UP,
 * otherwise the price frozen at publish (drops never lower the total).
 * `latestCostPaise` is null when the component could not be re-verified
 * against TBO (manual/DB components, expired inventory) - the stored price
 * stands in that case.
 */
export const SharePayNowComponentChangeSchema = z.object({
  componentId: UuidSchema,
  kind: z.enum(['FLIGHT', 'HOTEL']),
  title: z.string(),
  previousCostPaise: z.string(),
  latestCostPaise: z.string().nullable(),
  appliedCostPaise: z.string(),
  increased: z.boolean(),
});
export type SharePayNowComponentChange = z.infer<typeof SharePayNowComponentChangeSchema>;

/**
 * Pre-publish live price check (admin Preview tab). Mirrors the Pay-Now
 * reprice shape; `appliedCostPaise` is the latest live price when verifiable
 * (both increases and decreases apply before publish).
 */
export const PublishPriceCheckResponseSchema = z.object({
  status: z.enum(['UNCHANGED', 'CHANGED']),
  currencyCode: z.string(),
  currentGrandTotalPaise: z.string(),
  /** Estimated grand total after applying live component prices. */
  newGrandTotalPaise: z.string().nullable(),
  /** newGrandTotal - currentGrand; negative when costs dropped. */
  changeByPaise: z.string().nullable(),
  repriceToken: z.string().nullable(),
  repriceExpiresAt: IsoDateTimeSchema.nullable(),
  components: z.array(SharePayNowComponentChangeSchema),
});
export type PublishPriceCheckResponse = z.infer<typeof PublishPriceCheckResponseSchema>;

/**
 * Result of the pre-payment live price check. UNCHANGED means the customer
 * can pay on the existing link (`paymentLinkUrl`); INCREASED means the popup
 * must show `increaseByPaise` and, on confirm, POST `repriceToken` back to
 * get a fresh link for `newAmountPaise`.
 */
export const SharePayNowCheckResponseSchema = z.object({
  status: z.enum(['UNCHANGED', 'INCREASED']),
  currencyCode: z.string(),
  /** Existing hosted link - present when status is UNCHANGED. */
  paymentLinkUrl: z.string().nullable(),
  /** Amount on the current payment link. */
  currentAmountPaise: z.string(),
  /** New payable total after applying increases - INCREASED only. */
  newAmountPaise: z.string().nullable(),
  /** newAmountPaise - currentAmountPaise - INCREASED only. */
  increaseByPaise: z.string().nullable(),
  /** One-shot token to confirm the reprice - INCREASED only. */
  repriceToken: z.string().nullable(),
  /** ISO expiry of the reprice token - INCREASED only. */
  repriceExpiresAt: IsoDateTimeSchema.nullable(),
  components: z.array(SharePayNowComponentChangeSchema),
});
export type SharePayNowCheckResponse = z.infer<typeof SharePayNowCheckResponseSchema>;

export const SharePayNowConfirmRequestSchema = z.object({
  repriceToken: z.string().trim().min(1).max(128),
});
export type SharePayNowConfirmRequest = z.infer<typeof SharePayNowConfirmRequestSchema>;

export const SharePayNowConfirmResponseSchema = z.object({
  paymentLinkUrl: z.string(),
  amountPaise: z.string(),
  qrImageUrl: z.string().nullable(),
});
export type SharePayNowConfirmResponse = z.infer<typeof SharePayNowConfirmResponseSchema>;
